import React, { useEffect } from "react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import InnerHeader from "../../component/shared/innerheader/InnerHeader.js";
import { FormattedMessage, useIntl } from "react-intl";
import ChevronIcon from "svg-icons/chevron-icon.js";
import CustomizedDatePicker from "component/customizedDatePicker/customized-date-picker.js";
import useSWR from "swr";
import { END_POINTS } from "constants/APIConstant.js";
import { Gender, Degree } from "constants/Defaults.js";
import LabelLang, { LabelLangString } from "component/LabelLang/label-lang.js";
import fetch from "../../auth/FetchInterceptor.js";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import "./subscribeForm.scss";
import moment from "moment";
import { useSelector } from "react-redux";
import ContentLoading from "component/LoadingComponent/ContentLoading.js";
import MainDepartment from "component/shared/main-department/MainDepartment.jsx";
import CircularProgress from "@mui/material/CircularProgress";
import SnackbarModal from "component/shared/snackbar";
import { useNavigate } from "react-router-dom";
import { APP_PREFIX_PATH } from "configs/AppConfig.js";
import HtmlView from "component/htmlView/html-view.js";
import AboutDepartment from "component/shared/about-department/AboutDepartment.jsx";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ReactGA from "react-ga4";

const normalizeEgyptMobile = (value = "") => {
  const digits = value.replace(/\D/g, "");
  return digits.startsWith("20") ? digits.slice(2) : digits;
};

function SubscribeForm() {
  const dir = useSelector(({ theme }) => theme.direction);
  const currentLocale = useSelector(({ theme }) => theme.locale);

  const navigate = useNavigate();

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => {
      clearTimeout(timeOut);
    };
  }, []);

  ReactGA.initialize(END_POINTS.TRACKING_ID);

  useEffect(() => {
    // Send pageview with a custom path
    ReactGA.send({
      hitType: "pageview",
      page: "/register",
      title: "Register Page",
    });
  }, []);

  const formSchema = Yup.object().shape({
    password: Yup.string()
      .required(<FormattedMessage id="password-required-validation" />)
      // .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/, 'Password can only contain Latin letters.'),
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W,_]).{8,16}$/, {
        message: <FormattedMessage id="password-validation" />,
      }),
    reenterPassword: Yup.string()
      .required(<FormattedMessage id="password-required-validation" />)
      .oneOf(
        [Yup.ref("password")],
        <FormattedMessage id="password-match-validation" />,
      ),
    firstName: Yup.string()
      .matches(/^\S*$/, {
        message: <FormattedMessage id="first-name-validation" />,
      })
      .matches(/^[a-zA-Z\u0600-\u06FF,-][\sa-zA-Z\u0600-\u06FF,-]*$/, {
        message: <FormattedMessage id="first-name-validation" />,
      })
      .min(2)
      .max(20),
    lastName: Yup.string()
      .matches(/^\S*$/, {
        message: <FormattedMessage id="last-name-validation" />,
      })
      .matches(/^[a-zA-Z\u0600-\u06FF,-][\sa-zA-Z\u0600-\u06FF,-]*$/, {
        message: <FormattedMessage id="last-name-validation" />,
      })
      .min(2)
      .max(20),
    email: Yup.string()
      // .matches(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ , 'Please enter valid email'),
      .matches(
        // /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
        // /^[a-zA-Z0-9.!#$%&'*+=?^_]{3,25}(@[a-zA-Z0-9]{3,25}\.[a-zA-Z]{3,25})$/,
        // /^[a-zA-Z0-9.!#$%&'*+=?^_]{3,25}(@[a-zA-Z0-9]{3,25}\.[a-zA-Z0-9.!#$%&'*+=?^_]{3,25})$/,

        // Allow local-part (3-25) and domain with hyphens and multiple labels
        /^[a-zA-Z0-9.!#$%&'*+=?^_]{3,25}@(?:[a-zA-Z0-9-]{1,63}\.)+[a-zA-Z0-9.!#$%&'*+=?^_]{3,25}$/,
        { message: <FormattedMessage id="email-validation" /> },
      ),
    countryCode: Yup.string()
      .matches(
        /^[0-9]*$/,
        <FormattedMessage id="country-code-digit-validation" />,
      )
      .max(5, <FormattedMessage id="country-code-max-validation" />)
      .min(2, <FormattedMessage id="country-code-min-validation" />),
    mobile: Yup.string()
      .matches(/^[0-9]*$/)
      .max(15, <FormattedMessage id="mobile-max-validation" />)
      .min(7, <FormattedMessage id="mobile-min-validation" />),
    username: Yup.string()
      .matches(/^[a-zA-Z0-9.!#$%&'*+=?^_]{3,20}$/, {
        message: <FormattedMessage id="username-validation" />,
      })
      .min(3)
      .max(20),
    address: Yup.string().required(
      <FormattedMessage id="address-required-validation" />,
    ),
    dob: Yup.string()
      .required(<FormattedMessage id="dob-required-validation" />)
      .test(
        "is-valid-date",
        <FormattedMessage id="invalid-date-validation" />,
        function (value) {
          if (!value) return false;

          try {
            const date = new Date(value + "T00:00:00");
            return (
              !isNaN(date.getTime()) &&
              date >= new Date("1900-01-01") &&
              date <= new Date()
            );
          } catch (error) {
            return false;
          }
        },
      ),
  });

  const getCountries = useSWR({
    url: END_POINTS.COUNTRY,
  });

  const formOptions = {
    resolver: yupResolver(formSchema),
    defaultValues: {
      registrationType: "1",
      //country: defaultCountry,
    },
  };

  const {
    control,
    register,
    setValue,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState,
  } = useForm(formOptions);
  const { errors } = formState;

  useEffect(() => {
    let timeOut = setTimeout(() => {
      if (getCountries?.data?.data && getCountries?.data?.data !== null) {
        setValue("country", 225); //Egypt Country Code
      }
    }, 0);
    return () => {
      clearTimeout(timeOut);
    };
  }, [getCountries?.data]);

  const [data, setData] = useState("");
  const intl = useIntl();
  const onSubmit = (data) => {
    const normalizedMobile = String(data.mobile || "").replace(/\D/g, "");
    const payload = {
      ...data,
      mobile: normalizedMobile,
    };
    setData(JSON.stringify(payload));
    postLogin(payload);
  };

  const Countries =
    getCountries.error ||
    getCountries.isLoading ||
    getCountries.data === undefined ||
    getCountries.data === null
      ? []
      : getCountries.data.data.sort((a, b) => {
          if (dir === "rtl") {
            return a.name < b.name ? -1 : 1;
          } else {
            if (a.translations?.length > 0 || a.translations?.length > 0) {
              return a.translations[0]?.name < b.translations[0]?.name ? -1 : 1;
            }
            return a.name < b.name ? -1 : 1;
          }
        });

  const getSubscribtionType = useSWR({
    url: END_POINTS.SUBSCRIPTION_TYPE,
  });
  const SubscribtionType =
    getSubscribtionType.error ||
    getSubscribtionType.isLoading ||
    getSubscribtionType.data === undefined ||
    getSubscribtionType.data === null
      ? []
      : getSubscribtionType.data.data;

  const [show, setShow] = useState(false);
  const handleShow = () => {
    setShow(true);
  };
  const handleClose = () => {
    setShow(false);
  };

  const [success, setSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState("eg");

  const validateDobValue = (value) => {
    if (!value) {
      return;
    }

    const date = new Date(`${value}T00:00:00`);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (isNaN(date.getTime()) || date > now) {
      setError("dob", {
        type: "manual",
        message: intl.formatMessage({ id: "max-date-value" }),
      });
      return;
    }

    clearErrors("dob");
  };

  async function postLogin(data) {
    setButtonSpinner(true);

    // التحقق من صحة البيانات قبل الإرسال
    if (!data.dob) {
      setShowMessage("تاريخ الميلاد مطلوب");
      setSuccess(false);
      setButtonSpinner(false);
      handleShow();
      return;
    }

    const normalizedMobile = String(data.mobile || "").replace(/\D/g, "");
    const localEgyptMobile =
      normalizedMobile.startsWith("20")
        ? normalizedMobile.slice(2)
        : normalizeEgyptMobile(normalizedMobile);

    if (selectedPhoneCountry === "eg" && localEgyptMobile.length !== 11) {
      setShowMessage(intl.formatMessage({ id: "egypt-mobile-validation" }));
      setSuccess(false);
      setButtonSpinner(false);
      handleShow();
      return;
    }

    if (normalizedMobile.length > 0) {
      let loginData = {
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        phoneNumber:
          selectedPhoneCountry === "eg" ? localEgyptMobile : normalizedMobile,
        gender: data.gender,
        email: data.email,
        password: data.password,
        confirmPassword: data.reenterPassword,
        address: data.address,
        birthDate: data.dob
          ? (() => {
              try {
                // التحقق من صحة التاريخ
                const dateStr = data.dob;
                if (!dateStr || typeof dateStr !== "string") {
                  return null;
                }

                // استخدام التاريخ مباشرة بدون تحويل timezone
                // HTML5 date input يعطي التاريخ بصيغة YYYY-MM-DD
                const dateParts = dateStr.split("-");
                if (dateParts.length === 3) {
                  const year = parseInt(dateParts[0]);
                  const month = parseInt(dateParts[1]);
                  const day = parseInt(dateParts[2]);

                  // التحقق من صحة الأرقام
                  if (
                    year >= 1900 &&
                    year <= new Date().getFullYear() &&
                    month >= 1 &&
                    month <= 12 &&
                    day >= 1 &&
                    day <= 31
                  ) {
                    return dateStr; // إرجاع التاريخ كما هو
                  }
                }

                console.error("Invalid date format:", dateStr);
                return null;
              } catch (error) {
                console.error("Date processing error:", error);
                return null;
              }
            })()
          : null,
        degree: data.degree,
        subscriptionTypeId: data.registrationType,
        countryId: data.country,
      };

      return await fetch
        .post(END_POINTS.REGISTER, loginData)
        .then((res) => {
          const responseData = res?.data ?? res;
          setShowMessage(
            responseData?.message ||
              intl.formatMessage({ id: "successfully-register" }),
          );
          setSuccess(true);
          setButtonSpinner(false);
          handleShow();
          const userId = responseData?.id ?? responseData?.userId ?? null;
          const now = Date.now();
          const parsedResetTime = responseData?.resetTime
            ? new Date(responseData.resetTime).getTime()
            : NaN;
          const effectiveResetTime =
            !Number.isNaN(parsedResetTime) && parsedResetTime > now
              ? new Date(parsedResetTime).toISOString()
              : new Date(now + 10 * 60 * 1000).toISOString();
          const verificationData =
            userId != null ? { userId, resetTime: effectiveResetTime } : null;
          if (verificationData && typeof sessionStorage !== "undefined") {
            localStorage.setItem("emailVerificationCountDownTimer", effectiveResetTime);
            sessionStorage.setItem(
              "emailVerificationData",
              JSON.stringify(verificationData),
            );
          }
          setTimeout(() => {
            handleClose();
            if (verificationData) {
              navigate(`${APP_PREFIX_PATH}/verify-email`, {
                state: { verificationData },
              });
            } else {
              navigate(`${APP_PREFIX_PATH}/login`);
            }
          }, 1500);
        })
        .catch((e) => {
          console.error("Registration error:", e);
          setShowMessage(e.response?.data?.message || "حدث خطأ في التسجيل");
          setSuccess(false);
          setButtonSpinner(false);
          handleShow();
        });
    } else {
      setShowMessage(intl.formatMessage({ id: "mobile-required" }));
      setSuccess(false);
      setButtonSpinner(false);
      handleShow();
    }
  }

  const getDepartment = useSWR({
    url: `${END_POINTS.SERVICES_TOOLS_DEPARTMENT}=${END_POINTS.REGISTER_DEPARTMENT_ID}`,
  });

  const getServices = useSWR({
    url: `${END_POINTS.SERVICES_TOOLS}/${END_POINTS.REGISTER_DEPARTMENT_ID}`,
  });

  return (
    <ContentLoading
      isLoading={getDepartment.isLoading || getServices.isLoading}
    >
      <div className="subscribe">
        <InnerHeader
          breadcrumb={[
            {
              title: intl.formatMessage({ id: "service-tools" }),
              clickClass: false,
            },
            {
              title: intl.formatMessage({ id: "subscribe" }),
              clickClass: true,
            },
          ]}
          title={intl.formatMessage({ id: "subscribe-to-CAPMAS" })}
          route={"../servicestools"}
        />
        <div className="container py-5">
          <div className="row gy-lg-0 gy-4">
            <div className="col-12 col-lg-8">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      <FormattedMessage id="registration-type" />:
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div className="form-label-container dropdown">
                      <select
                        className={`${dir === "rtl" ? "r-dir" : "l-dir"}`}
                        {...register("registrationType")}
                      >
                        {SubscribtionType.map(
                          ({ id, title, subscriptionTypeTranslations }, i) => (
                            <option
                              key={i}
                              value={id}
                              title={LabelLangString({
                                id: "title",
                                currentLocale: currentLocale,
                                defaultValue: title,
                                translations: subscriptionTypeTranslations,
                              })}
                            >
                              <LabelLang
                                id={"title"}
                                defaultValue={title}
                                translations={subscriptionTypeTranslations}
                              />
                            </option>
                          ),
                        )}
                      </select>
                      <span>*</span>
                    </div>
                  </div>
                </div>
                <br></br>

                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      <FormattedMessage id="fname" />:
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div
                      className={`form-label-container ${
                        errors?.firstName?.message ? "mb-3" : ""
                      }`}
                    >
                      <input
                        type="text"
                        {...register("firstName")}
                        className={`form-control ${
                          errors.firstName ? "is-invalid px-4" : ""
                        }`}
                      />
                      <span>*</span>
                    </div>
                    {errors?.firstName?.message && (
                      <alert className="alert alert-danger py-1">
                        {errors?.firstName?.message}
                      </alert>
                    )}
                  </div>
                </div>
                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      <FormattedMessage id="lname" />:{" "}
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div
                      className={`form-label-container ${
                        errors?.lastName?.message ? "mb-3" : ""
                      }`}
                    >
                      <input
                        type="text"
                        {...register("lastName")}
                        className={`form-control ${
                          errors.lastName ? "is-invalid px-4" : ""
                        }`}
                      />
                      <span>*</span>
                    </div>
                    {errors?.lastName?.message && (
                      <alert className="alert alert-danger py-1">
                        {errors?.lastName?.message}
                      </alert>
                    )}
                  </div>
                </div>
                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      <FormattedMessage id="gender" />:
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div className="form-label-container dropdown">
                      <select
                        className={`${dir === "rtl" ? "r-dir" : "l-dir"}`}
                        {...register("gender")}
                      >
                        {Gender.map(({ id, name, Translation }, i) => (
                          <option
                            key={i}
                            value={id}
                            title={LabelLangString({
                              id: "name",
                              currentLocale: currentLocale,
                              defaultValue: name,
                              translations: Translation,
                            })}
                          >
                            <LabelLang
                              id={"name"}
                              defaultValue={name}
                              translations={Translation}
                            />
                          </option>
                        ))}
                      </select>
                      <span>*</span>
                    </div>
                  </div>
                </div>
                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      <FormattedMessage id="dob" />:
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div
                      className={`form-label-container ${
                        errors?.dob?.message ? "mb-3" : ""
                      }`}
                    >
                      <Controller
                        control={control}
                        name="dob"
                        render={({ field }) => (
                          <CustomizedDatePicker
                            dateFormat="dd/MM/yyyy"
                            placeholderText={intl.formatMessage({
                              id: "select-date",
                            })}
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value);
                              validateDobValue(value);
                            }}
                            value={field.value}
                            minDate={new Date("1900-01-01")
                              .toJSON()
                              .slice(0, 10)}
                            maxDate={new Date().toJSON().slice(0, 10)}
                            className={`form-control ${
                              errors.dob ? "is-invalid px-4" : ""
                            }`}
                          />
                        )}
                      />
                      <span>*</span>
                    </div>
                    {errors?.dob?.message && (
                      <alert className="alert alert-danger py-1">
                        {errors?.dob?.message}
                      </alert>
                    )}
                  </div>
                </div>
                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      <FormattedMessage id="educational-qualification" />:
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div className="form-label-container dropdown">
                      <select
                        className={`${dir === "rtl" ? "r-dir" : "l-dir"}`}
                        {...register("degree")}
                      >
                        {Degree.map(({ id, name, Translation }, i) => (
                          <option
                            key={i}
                            value={id}
                            title={LabelLangString({
                              id: "name",
                              currentLocale: currentLocale,
                              defaultValue: name,
                              translations: Translation,
                            })}
                          >
                            <LabelLang
                              id={"name"}
                              defaultValue={name}
                              translations={Translation}
                            />
                          </option>
                        ))}
                      </select>
                      <span>*</span>
                    </div>
                  </div>
                </div>
                <br></br>

                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      {" "}
                      <FormattedMessage id="email" />:{" "}
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div
                      className={`form-label-container ${
                        errors?.email?.message ? "mb-3" : ""
                      }`}
                    >
                      <input
                        type="email"
                        {...register("email")}
                        className={`form-control ${
                          errors.email ? "is-invalid px-4" : ""
                        }`}
                      />
                      <span>*</span>
                    </div>
                    {errors?.email?.message && (
                      <alert className="alert alert-danger py-1">
                        {errors?.email?.message}
                      </alert>
                    )}
                  </div>
                </div>
                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      {" "}
                      <FormattedMessage id="country" />:
                    </label>
                  </div>
                  {Countries.length > 0 && (
                    <div className="col-xl-8 col-lg-9">
                      <div className="form-label-container dropdown">
                        <select
                          className={`${dir === "rtl" ? "r-dir" : "l-dir"}`}
                          {...register("country")}
                          //defaultValue={defaultCountry}
                        >
                          {Countries.map(
                            ({ id, name, countryTranslations }, i) => (
                              <option
                                key={i}
                                value={id}
                                //defaultChecked={id === defaultCountry}
                                title={LabelLangString({
                                  id: "name",
                                  currentLocale: currentLocale,
                                  defaultValue: name,
                                  translations: countryTranslations,
                                })}
                              >
                                <LabelLang
                                  id={"name"}
                                  defaultValue={name}
                                  translations={countryTranslations}
                                />
                              </option>
                            ),
                          )}
                        </select>
                        <span>*</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      {" "}
                      <FormattedMessage id="address" />:
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div
                      className={`form-label-container ${
                        errors?.address?.message ? "mb-3" : ""
                      }`}
                    >
                      <input
                        type="text"
                        {...register("address")}
                        className={`form-control ${
                          errors.address ? "is-invalid px-4" : ""
                        }`}
                      />
                      <span>*</span>
                    </div>
                    {errors?.address?.message && (
                      <alert className="alert alert-danger py-1">
                        {errors?.address?.message}
                      </alert>
                    )}
                  </div>
                </div>
                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      <FormattedMessage id="mobile" />:
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div className="row">
                      <div className="col-lg-12 position-relative">
                        <div
                          className={`form-label-container mobile ${
                            errors?.mobile?.message ? "mb-3" : ""
                          }`}
                          dir={"ltr"}
                        >
                          {/* <input
                            {...register("countryCode")}
                            className={`form-control ${
                              errors.countryCode ? "is-invalid" : ""
                            }`}
                          /> */}
                          <Controller
                            control={control}
                            name="mobile"
                            render={({ field: { ref, ...field } }) => (
                              <PhoneInput
                                name={field.name}
                                value={field.value || ""}
                                onChange={(value, countryData) => {
                                  const digits = String(value || "").replace(/\D/g, "");
                                  const dialCode = String(countryData?.dialCode || "");
                                  const countryCode = String(
                                    countryData?.countryCode || selectedPhoneCountry
                                  );
                                  setSelectedPhoneCountry(countryCode);

                                  const localNumber =
                                    countryCode === "eg"
                                      ? digits.startsWith(dialCode || "20")
                                        ? digits.slice((dialCode || "20").length)
                                        : normalizeEgyptMobile(digits)
                                      : digits;

                                  if (countryCode === "eg" && localNumber.length > 11) {
                                    setError("mobile", {
                                      type: "manual",
                                      message: intl.formatMessage({
                                        id: "egypt-mobile-validation",
                                      }),
                                    });
                                    return;
                                  }

                                  clearErrors("mobile");
                                  field.onChange(digits);
                                }}
                                country={"eg"}
                                inputProps={{
                                  ref,
                                  required: true,
                                  autoFocus: true,
                                  onKeyDown: (e) => {
                                    const allowedKeys = [
                                      "Backspace",
                                      "Delete",
                                      "ArrowLeft",
                                      "ArrowRight",
                                      "Tab",
                                      "Home",
                                      "End",
                                      "Control",
                                      "Meta",
                                      "Alt",
                                      "Shift",
                                    ];
                                    const isDigit = /^[0-9]$/.test(e.key);
                                    // Allow all Ctrl/Cmd shortcuts regardless of keyboard layout
                                    if (e.ctrlKey || e.metaKey) {
                                      return;
                                    }
                                    const currentDigits = String(
                                      field.value || ""
                                    ).replace(/\D/g, "");
                                    const localLength =
                                      currentDigits.startsWith("20")
                                        ? currentDigits.length - 2
                                        : normalizeEgyptMobile(currentDigits).length;
                                    if (
                                      isDigit &&
                                      selectedPhoneCountry === "eg" &&
                                      localLength >= 11
                                    ) {
                                      e.preventDefault();
                                      setError("mobile", {
                                        type: "manual",
                                        message: intl.formatMessage({
                                          id: "egypt-mobile-validation",
                                        }),
                                      });
                                      return;
                                    }
                                    if (
                                      !isDigit &&
                                      !allowedKeys.includes(e.key) &&
                                      !(e.ctrlKey || e.metaKey)
                                    ) {
                                      e.preventDefault();
                                    }
                                  },
                                }}
                                inputStyle={{
                                  direction: "ltr",
                                }}
                                // dropdownStyle={{
                                //   direction:
                                //     currentLocale == "ar" ? "rtl" : "ltr",
                                // }}

                                // searchStyle={{
                                //   paddingInlineEnd:
                                //     currentLocale == "ar" ? "40px" : "0px",
                                //   paddingInlineStart:
                                //     currentLocale == "ar" ? "0px" : "40px",
                                // }}
                                placeholder="+201121221501"
                                className={`form-control ${
                                  errors.mobile ? "is-invalid px-4" : ""
                                }`}
                              />
                            )}
                          />
                        </div>
                        <span
                          className=" position-absolute"
                          style={{
                            top: "10px",
                            insetInlineEnd: "0px",
                            color: "red",
                          }}
                        >
                          *
                        </span>
                      </div>
                      {/* <div className="col-lg-9">
                        <div
                          className={`form-label-container ${
                            errors?.countryCode?.message ? "mb-3" : ""
                          }`}
                        >
                          <input
                            {...register("mobile")}
                            className={`form-control ${
                              errors.mobile ? "is-invalid" : ""
                            }`}
                          />
                          <span>*</span>
                        </div>
                      </div> */}
                    </div>
                    <div className="col-12 mb-3">
                      {errors?.mobile?.message && (
                        <alert className="alert alert-danger py-1">
                          {errors?.mobile?.message}
                        </alert>
                      )}
                    </div>
                    <div className="col-12">
                      {errors?.countryCode?.message && (
                        <alert className="alert alert-danger py-1">
                          {errors?.countryCode?.message}
                        </alert>
                      )}
                    </div>
                  </div>
                </div>
                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      {" "}
                      <FormattedMessage id="username" />:
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div
                      className={`form-label-container ${
                        errors?.username?.message ? "mb-3" : ""
                      }`}
                    >
                      <input
                        type="text"
                        {...register("username")}
                        className={`form-control ${
                          errors.username ? "is-invalid px-4" : ""
                        }`}
                      />
                      <span>*</span>
                    </div>
                    {errors?.username?.message && (
                      <alert className="alert alert-danger py-1">
                        {errors?.username?.message}
                      </alert>
                    )}
                  </div>
                </div>
                <div className="form-label row">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      <FormattedMessage id="password" />:{" "}
                    </label>{" "}
                    {/* {errors.password && <p style={{color:"red"}}>(Incorrect)</p>} */}
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div
                      className={`form-label-container ${
                        errors?.password?.message ? "mb-3" : ""
                      }`}
                      style={{ position: "relative" }}
                    >
                      <input
                        type={showPassword ? "text" : "password"}
                        {...register("password")}
                        className={`form-control ${
                          errors.password ? "is-invalid px-4" : ""
                        }`}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: dir === "rtl" ? "auto" : "20px",
                          left: dir === "rtl" ? "20px" : "auto",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "16px",
                          color: "#666",
                        }}
                      >
                        {showPassword ? "👁️" : "👁️‍🗨️"}
                      </button>
                      <span>*</span>
                    </div>
                    {errors?.password?.message && (
                      <alert className="alert alert-danger py-1">
                        {errors?.password?.message}
                      </alert>
                    )}
                  </div>
                </div>
                <div className="form-label row mb-4">
                  <div className="col-xl-4 col-lg-3">
                    <label>
                      {" "}
                      <FormattedMessage id="reenter-password" />:
                    </label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                    <div
                      className={`form-label-container ${
                        errors?.reenterPassword?.message ? "mb-3" : ""
                      }`}
                      style={{ position: "relative" }}
                    >
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        {...register("reenterPassword")}
                        className={`form-control ${
                          errors.reenterPassword ? "is-invalid px-4" : ""
                        }`}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        style={{
                          position: "absolute",
                          right: dir === "rtl" ? "auto" : "20px",
                          left: dir === "rtl" ? "20px" : "auto",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "16px",
                          color: "#666",
                        }}
                      >
                        {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                      </button>
                      <span>*</span>
                    </div>
                    {errors?.reenterPassword?.message && (
                      <alert className="alert alert-danger py-1">
                        {errors?.reenterPassword?.message}
                      </alert>
                    )}
                  </div>
                </div>
                {/* <div className="form-label row">
                    <div className="col-xl-4 col-lg-3">
                  <label> <FormattedMessage id="verification-code"/>:</label>
                  </div>
                  <div className="col-xl-8 col-lg-9">
                  <div className="form-label-container">
                    <input type="text" {...register("verificationCode")} />
                    <span>*</span>
                  </div>
                  </div>
                </div> */}

                <div className="text-center">
                  <button
                    type="submit"
                    className="main-btn"
                    style={{ marginInlineStart: "auto" }}
                  >
                    <span>
                      {buttonSpinner ? (
                        <span>
                          <CircularProgress sx={{ color: "#fff" }} size={26} />
                        </span>
                      ) : (
                        <>
                          <span style={{ marginInlineEnd: "0.5rem" }}>
                            <FormattedMessage id="register" />
                          </span>
                          <ChevronIcon width={12} height={12} color="#FFFFFF" />
                        </>
                      )}
                    </span>
                  </button>
                </div>

                <SnackbarModal
                  open={show}
                  onClose={handleClose}
                  severity={success ? "success" : "error"}
                  message={showMessage}
                />
              </form>
            </div>
            <div className="col-12 col-lg-4">
              <AboutDepartment
                id={"about"}
                txt={getServices?.data?.data?.about}
                translations={
                  getServices?.data?.data?.serviceAndToolTranslation
                }
              />
            </div>
          </div>
          <div className="row mt-5">
            <div className="col-12 col-lg-8">
              <div className="calnd-info">
                {getServices?.data?.data?.content !== null &&
                getServices?.data?.data?.content !== undefined ? (
                  <HtmlView
                    html={LabelLangString({
                      id: "content",
                      currentLocale: currentLocale,
                      defaultValue: getServices?.data?.data?.content,
                      translations:
                        getServices?.data?.data?.serviceAndToolTranslation,
                    })}
                  />
                ) : (
                  <FormattedMessage id="data-not-found" />
                )}
              </div>
            </div>
            <div className="col-12 col-lg-4">
              <MainDepartment getDepartment={getDepartment} />
            </div>
          </div>
        </div>
      </div>
    </ContentLoading>
  );
}

export default SubscribeForm;
